import React from 'react'
import { Check, FileText, Film, Image as ImageIcon, Music, Box, Code, MoreVertical, Trash2 } from 'lucide-react'
import { cn } from '../lib/utils'
import { Button } from '@iws/admin-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@iws/admin-ui/dropdown-menu'
import { MediaFile } from '../types'

const getFileIcon = (type: string) => {
  if (type.includes('image')) return <ImageIcon className="size-6 text-muted-foreground" />
  if (type.includes('video')) return <Film className="size-6 text-muted-foreground" />
  if (type.includes('audio')) return <Music className="size-6 text-muted-foreground" />
  if (type.includes('pdf')) return <FileText className="size-6 text-muted-foreground" />
  if (type.includes('text') || type.includes('code') || type.includes('json')) return <Code className="size-6 text-muted-foreground" />
  return <Box className="size-6 text-muted-foreground" />
}

const IMAGE_EXT = /\.(avif|bmp|gif|ico|jpe?g|png|svg|webp)$/i

interface MediaFileItemProps {
  file: MediaFile
  viewMode?: 'grid' | 'list'
  resolveUrl: (path: string) => string
  formatSize: (bytes: number) => string
  selectable?: boolean
  selected?: boolean
  onSelect?: () => void
  onActivate?: () => void
  onDelete?: (id: string) => void
  canDelete?: boolean
}

export const MediaFileItem = ({
  file,
  viewMode = 'grid',
  resolveUrl,
  formatSize,
  selectable = false,
  selected = false,
  onSelect,
  onActivate,
  onDelete,
  canDelete = false,
}: MediaFileItemProps) => {
  const isImage = file.type?.includes('image') || IMAGE_EXT.test(file.name)
  const fileUrl = resolveUrl(file.path)
  const extension = (file.name.split('.').pop() ?? file.type?.split('/')[1] ?? '').toUpperCase()

  const handleMainClick = () => {
    if (selectable && onSelect) {
      onSelect()
    }
  }

  const interactiveProps = {
    role: selectable ? 'checkbox' : undefined,
    'aria-checked': selectable ? selected : undefined,
    'aria-label': file.name,
    tabIndex: 0,
    onClick: handleMainClick,
    onDoubleClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.target !== e.currentTarget) return
      if (e.key === ' ') {
        e.preventDefault()
        handleMainClick()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (onActivate) onActivate()
        else handleMainClick()
      }
    },
  }

  if (viewMode === 'list') {
    return (
      <div
        className={cn(
          'group flex cursor-pointer items-center gap-3 border-b border-border-subtle px-3 py-2 outline-none transition-colors last:border-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          selected ? 'bg-fill' : 'hover:bg-subdued',
        )}
        {...interactiveProps}
      >
        {selectable && (
          <span
            aria-hidden="true"
            className={cn(
              'grid size-4 shrink-0 place-items-center rounded border',
              selected ? 'border-ink bg-ink text-primary-foreground' : 'border-input bg-card',
            )}
          >
            {selected && <Check className="size-3" strokeWidth={3} />}
          </span>
        )}
        <div className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-md border border-border-subtle bg-subdued">
          {isImage ? (
            <img src={fileUrl} alt="" className="size-full object-cover" loading="lazy" />
          ) : (
            getFileIcon(file.type)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-ink">
            {file.name}
          </p>
          <p className="text-xs tabular-nums text-text-2">{formatSize(file.size)}</p>
        </div>
        <span className="hidden shrink-0 text-sm text-text-2 sm:block">
          {extension}
        </span>
        {canDelete && onDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="File actions" className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-error focus:bg-error-lighter focus:text-error-dark"
                onClick={(e) => { e.stopPropagation(); onDelete(file._id) }}
              >
                <Trash2 className="mr-2 size-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'group relative cursor-pointer overflow-hidden rounded-lg border bg-card outline-none transition-[border-color,box-shadow] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        selected ? 'border-ink ring-1 ring-ink' : 'border-border-subtle hover:border-border hover:shadow-card',
      )}
      title={file.name}
      {...interactiveProps}
    >
      {/* Delete menu top-right */}
      {canDelete && onDelete && (
        <div className="absolute right-2 top-2 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 group-focus-visible:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="File actions"
                className="shadow-card"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-error focus:bg-error-lighter focus:text-error-dark"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(file._id)
                }}
              >
                <Trash2 className="mr-2 size-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="relative grid aspect-square place-items-center overflow-hidden border-b border-border-subtle bg-subdued">
        {isImage ? (
          <img
            src={fileUrl}
            alt=""
            className="size-full object-contain p-2"
            loading="lazy"
          />
        ) : (
          getFileIcon(file.type)
        )}

        {/* Selection indicator top-left */}
        {selectable && (
          <div
            className={cn(
              'absolute left-2 top-2 grid size-5 place-items-center rounded border transition-opacity',
              selected
                ? 'border-ink bg-ink text-primary-foreground'
                : 'border-input bg-card opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
            )}
          >
            {selected && <Check className="size-3" strokeWidth={3} />}
          </div>
        )}
      </div>

      <div className="px-2.5 py-2">
        <p className="truncate text-sm text-ink">{file.name}</p>
        <p className="mt-0.5 truncate text-xs tabular-nums text-text-2">
          {extension} · {formatSize(file.size)}
        </p>
      </div>
    </div>
  )
}
